// Shipping estimates from Shopify itself - no shipping amount lives in the theme (owner decision).
// Reads the JSON config rendered by snippets/shipping-config.liquid and exposes window.SBGShipping:
//
//   SBGShipping.estimateForCart()            → Promise<{ price, name, free, currency } | null>
//     Cheapest rate for the visitor's current cart to their localization country, via the Ajax
//     API (/cart/prepare_shipping_rates.json, then polling /cart/async_shipping_rates.json).
//     price is in cents; free === true when the cheapest rate costs 0. null when the cart is
//     empty, no rate applies or the request fails.
//
//   SBGShipping.estimateForVariant(variantId, quantity = 1) → Promise<{ price, name, free, currency } | null>
//     Cheapest delivery option for one variant, via a throwaway Storefront API cart (cartCreate
//     with the visitor's country) - the visitor's real cart is never touched. Needs the public
//     token in settings.storefront_access_token; resolves null without it or on any failure.
//
//   SBGShipping.freeShippingThreshold(source?) → number (cents) | null
//     source: an element (or a selector) carrying data-free-shipping-threshold (the product's
//     custom.free_shipping_threshold metafield, see snippets/shipping-config.liquid) or a number.
//     Falls back to the shop metafield custom.free_shipping_threshold; null when neither is set.
//
//   SBGShipping.formatMoney(cents) → string in the shop's money format ("€6,95").
(function () {
  const configEl = document.getElementById("SBGShippingConfig");
  let config = {};
  try {
    config = configEl ? JSON.parse(configEl.textContent) : {};
  } catch {
    config = {};
  }
  const root = (config.rootUrl || "/").replace(/\/?$/, "/");
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const toCents = (amount) => Math.round(parseFloat(amount) * 100);

  function formatMoney(cents, format = config.moneyFormat || "€{{amount_with_comma_separator}}") {
    if (!Number.isFinite(cents)) return "";
    const fixed = (cents / 100).toFixed(2);
    const [whole, fraction] = fixed.split(".");
    const withSep = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const values = {
      amount: `${withSep(",")}.${fraction}`,
      amount_no_decimals: withSep(","),
      amount_with_comma_separator: `${withSep(".")},${fraction}`,
      amount_no_decimals_with_comma_separator: withSep("."),
      amount_with_apostrophe_separator: `${withSep("'")}.${fraction}`,
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => values[key] ?? match);
  }

  function cheapest(rates) {
    if (!rates.length) return null;
    const best = rates.reduce((a, b) => (b.price < a.price ? b : a));
    return { ...best, free: best.price === 0 };
  }

  async function estimateForCart() {
    if (!config.countryCode) return null;
    try {
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } })).json();
      if (!cart.item_count) return null;
      const params = new URLSearchParams({ "shipping_address[country]": config.countryCode });
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, { method: "POST" });
      if (!prepared.ok) return null;
      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`);
        if (!response.ok) return null;
        const data = await response.json();
        if (data && Array.isArray(data.shipping_rates)) {
          return cheapest(
            data.shipping_rates.map((rate) => ({
              price: toCents(rate.price),
              name: rate.presentment_name || rate.name,
              currency: rate.currency || cart.currency,
            }))
          );
        }
        await sleep(500);
      }
    } catch {
      // fall through
    }
    return null;
  }

  const ESTIMATE_MUTATION = `mutation SbgShippingEstimate($lines: [CartLineInput!]!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: {
      lines: $lines,
      buyerIdentity: { countryCode: $country },
      delivery: { addresses: [{ selected: true, address: { deliveryAddress: { countryCode: $country } } }] }
    }) {
      cart { deliveryGroups(first: 5) { nodes { deliveryOptions { title handle estimatedCost { amount currencyCode } } } } }
      userErrors { field message }
    }
  }`;

  async function estimateForVariant(variantId, quantity = 1) {
    if (!config.storefrontToken || !config.storefrontEndpoint || !config.countryCode || !variantId) return null;
    try {
      const response = await fetch(config.storefrontEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.storefrontToken,
        },
        body: JSON.stringify({
          query: ESTIMATE_MUTATION,
          variables: {
            country: config.countryCode,
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
          },
        }),
      });
      if (!response.ok) return null;
      const { data } = await response.json();
      const groups = data?.cartCreate?.cart?.deliveryGroups?.nodes || [];
      const rates = groups.flatMap((group) =>
        (group.deliveryOptions || [])
          .filter((option) => option.estimatedCost)
          .map((option) => ({
            price: toCents(option.estimatedCost.amount),
            name: option.title,
            currency: option.estimatedCost.currencyCode,
          }))
      );
      return cheapest(rates);
    } catch {
      return null;
    }
  }

  function freeShippingThreshold(source) {
    if (Number.isFinite(source)) return source;
    const el = typeof source === "string" ? document.querySelector(source) : source;
    const own = el?.closest?.("[data-free-shipping-threshold]")?.dataset.freeShippingThreshold;
    if (own) return Number(own);
    return Number.isFinite(config.shopThreshold) ? config.shopThreshold : null;
  }

  window.SBGShipping = { estimateForCart, estimateForVariant, freeShippingThreshold, formatMoney, config };
})();
